// src/components/UserProfilePopover.js
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ProfileUpdate from '../pages/user/ProfileUpdate';
import { userAuthAPI } from '../services/api';
import './UserProfilePopover.css';

// Shared account-details logic (user data, remaining-days/status
// calculation, logout) — used by both the desktop floating popover below
// and, standalone, by Configure's mobile bottom sheet (see
// AccountDetailsPanel export + Configure/index.js's "account" mobileSheet,
// wired from the settings icon now living in MobileBottomToolbar per
// explicit client request — a *sheet* like every other stage, not a small
// popover).
function useAccountDetails() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user_data');
    if (userData) setUser(JSON.parse(userData));
  }, []);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await userAuthAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user_data');
      navigate('/login');
    }
  };

  const calculateRemainingDays = () => {
    if (!user?.plan_end_date) return 0;
    const endDate = new Date(user.plan_end_date);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const getStatusColor = () => {
    const days = calculateRemainingDays();
    if (days === 0) return '#ef4444';
    if (days <= 7) return '#f59e0b';
    return '#10b981';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  return { user, loading, handleLogout, calculateRemainingDays, getStatusColor, formatDate };
}

/**
 * The actual account-details content (avatar, name, email, phone,
 * subscription status, logout button) with NO chrome of its own — no
 * trigger button, no popover positioning. Rendered as-is inside the
 * desktop popover below, and, separately, inside Configure's mobile bottom
 * sheet (wrapped in MobileSheetWrapper there instead).
 */
export function AccountDetailsPanel({ onUpdateProfile }) {
  const { user, loading, handleLogout, calculateRemainingDays, getStatusColor, formatDate } = useAccountDetails();
  if (!user) return null;
  const remainingDays = calculateRemainingDays();
  const statusColor = getStatusColor();

  return (
    <>
      <div className="profile-header">
        <div className="user-avatar-circle">{user.name.charAt(0).toUpperCase()}</div>
        <div className="user-details">
          <strong>{user.name}</strong>
          <span>{user.email}</span>
        </div>
      </div>

      <div className="profile-section">
        <div className="profile-item">
          <i className="bi bi-telephone-fill"></i>
          <span>{user.mobile || 'N/A'}</span>
        </div>
      </div>

      <div className="profile-section plan-section">
        <div className="plan-status">
          <div className="status-badge" style={{ background: `${statusColor}15`, color: statusColor }}>
            <i className="bi bi-circle-fill"></i>
            {remainingDays === 0 ? 'Expired' : remainingDays <= 7 ? 'Expiring Soon' : 'Active'}
          </div>
          <div className="section-label">
            <i className="bi bi-calendar-check-fill"></i>
            Subscription
          </div>
        </div>
        <div className="plan-details">
          <div className="plan-detail-item">
            <span className="detail-label">Remaining</span>
            <span className="detail-value" style={{ color: statusColor }}>{remainingDays} days</span>
          </div>
          <div className="plan-detail-item">
            <span className="detail-label">Valid Until</span>
            <span className="detail-value">{formatDate(user.plan_end_date)}</span>
          </div>
        </div>
      </div>

      {onUpdateProfile && (
        <button type="button" className="profile-update-btn" onClick={onUpdateProfile}>
          <i className="bi bi-person-lines-fill"></i>
          Update Profile
        </button>
      )}

      <button className="logout-btn" onClick={handleLogout} disabled={loading}>
        <i className="bi bi-box-arrow-right"></i>
        {loading ? 'Logging out...' : 'Logout'}
      </button>
    </>
  );
}

/**
 * Full "Update Profile" form (the same ProfileUpdate screen Settings uses)
 * shown as a modal over whatever page is open — used from the editor so the
 * user does not have to leave the canvas. Rendered in a portal so it is not
 * affected by the popover closing.
 */
export function ProfileUpdateModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div className="profile-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <ProfileUpdate onClose={onClose} />
    </div>,
    document.body
  );
}

/** Account details + Update Profile button/modal in one piece (used by Configure's mobile account sheet). */
export function AccountDetailsWithProfile() {
  const [profileOpen, setProfileOpen] = useState(false);
  return (
    <>
      <AccountDetailsPanel onUpdateProfile={() => setProfileOpen(true)} />
      {profileOpen && <ProfileUpdateModal onClose={() => setProfileOpen(false)} />}
    </>
  );
}

// Desktop's floating gear-icon popover — unchanged from before any mobile
// work started. Editor.js only mounts this on desktop now (isMobile guards
// it out there); mobile's equivalent lives entirely in Configure's own
// bottom toolbar + sheet instead.
const UserProfilePopover = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const popoverRef = useRef(null);
  const { user } = useAccountDetails();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div className="profile-wrapper" ref={popoverRef}>
      <button className="profile-btn" onClick={() => setIsOpen(!isOpen)} title="Account Settings">
        <i className="bi bi-gear-fill"></i>
      </button>
      {isOpen && (
        <div className="profile-popover">
          <AccountDetailsPanel onUpdateProfile={() => { setIsOpen(false); setProfileOpen(true); }} />
        </div>
      )}
      {profileOpen && <ProfileUpdateModal onClose={() => setProfileOpen(false)} />}
    </div>
  );
};

export default UserProfilePopover;
